import React, {
  memo,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import Button, { IButton } from "../Button/Button";
import clsx from "clsx";
import { MdMoreHoriz } from "react-icons/md";
import { Tooltip } from "react-tooltip";
import { ICON_SIZES } from "../../helpers/constants";
import { motion } from "framer-motion";
import useClickAway from "src/hooks/useClickAway";
import { createPortal } from "react-dom";

export interface IButtonWithMenu extends Omit<IButton, "onClick"> {
  options: Array<IButton & { keepOpenOnClick?: boolean }>;
  menuPosition?: "bottom" | "top";
  onClick?: (e: any) => void;
}

const ButtonWithMenu: React.FC<IButtonWithMenu> = ({
  options,
  menuPosition = "bottom",
  ...props
}) => {
  const [isPending, startTransition] = useTransition();
  const {
    icon = <MdMoreHoriz size={ICON_SIZES.MD} />,
    tooltip = "",
    hide = false,
    className = "",
    text = "",
    isLoading = false,
  } = props;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const tooltipId = useId();
  const ref = useRef(null);

  useEffect(() => {
    return () => setIsOpen(false);
  }, []);

  const menuItems = useMemo(() => {
    return options.filter((option) => !option?.hide);
  }, [options]);

  useClickAway(ref, () => {
    startTransition(() => setIsOpen(false));
  });

  useEffect(() => {
    const body = document?.getElementsByTagName("body")?.[0];

    if (isOpen) {
      body?.classList?.add?.("expenser-block-scroll");
    }

    return () => {
      body?.classList?.remove?.("expenser-block-scroll");
    };
  }, [isOpen]);

  if (hide) {
    return null;
  }

  return (
    <>
      {isOpen &&
        createPortal(
          <div
            className={clsx([
              "fixed",
              "top-0",
              "left-0",
              "right-0",
              "bottom-0",
              "bg-transparent",
              "z-0",
              "hidden",
              "md:flex",
            ])}
            onClick={() => {
              setIsOpen(false);
            }}
          />,
          document.body
        )}
      <div
        className={clsx([
          "relative",
          "text-base",
          "text-gray-500",
          "dark:text-gray-300",
        ])}
        ref={ref}
      >
        <Button
          type="button"
          onClick={() => startTransition(() => setIsOpen(!isOpen))}
          tooltip={tooltip}
          icon={icon}
          className={className}
          text={text}
          isLoading={isLoading}
          disabled={isLoading}
        />
        {/* <SlideDownUpAnimation show={isOpen}> */}
        {isOpen && (
          <>
            <ul
              className={clsx([
                "absolute",
                "right-0",
                "bg-white",
                "dark:bg-gray-900",
                "md:border dark:border-gray-800",
                "rounded-md md:rounded-lg",
                "overflow-x-hidden",
                "overflow-y-auto",
                "no-scrollbar",
                "shadow-md",
                "dark:shadow-gray-950",
                "min-w-[170px]",
                "max-h-[60vh]",
                {
                  "top-[calc(100%+4px)]": menuPosition === "bottom",
                  "bottom-[calc(100%+4px)]": menuPosition === "top",
                },
                "z-10",
              ])}
            >
              {menuItems.map((option, index: number) => (
                <motion.li
                  key={index}
                  onClick={(e: any) =>
                    startTransition(() => {
                      if (!option?.disabled) {
                        option?.onClick?.(e);
                        if (!option?.keepOpenOnClick) {
                          setIsOpen(false);
                        }
                      }
                    })
                  }
                  initial={{
                    opacity: 0,
                    y: -10,
                  }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.09,
                    ease: "easeOut",
                    delay: index * 0.05,
                  }}
                  data-tooltip-id={tooltipId}
                  data-tooltip-content={option?.tooltip}
                  data-tooltip-float
                  data-tooltip-position-strategy="fixed"
                  data-tooltip-offset={20}
                  className={clsx([
                    "px-2 pr-4 md:px-4 md:pr-6",
                    "py-3 md:py-3",
                    "whitespace-nowrap",
                    {
                      "border-b border-b-gray-50": index < menuItems.length - 1,
                      "dark:border-b-gray-800": true,
                      "dark:hover:bg-gray-700 hover:bg-blue-100 hover:text-black hover:dark:text-white":
                        !option?.disabled,
                      "cursor-pointer ": !option?.disabled,
                      "cursor-not-allowed text-gray-400": option?.disabled,
                    },
                    "flex",
                    "flex-row",
                    "gap-1",
                    "md:gap-3",
                    "items-center",
                    "text-sm",
                    "md:text-base",
                    option?.className || "",
                    "group",
                  ])}
                >
                  <span
                    className={clsx([
                      "group-hover:animate-pulse",
                      "shrink-0",
                      "min-w-[34px]",
                      "max-w-[34px]",
                      "dark:text-cyan-400 text-blue-500",
                      {
                        "opacity-20": option?.disabled,
                      },
                    ])}
                  >
                    {option.icon}
                  </span>
                  {option.text}
                </motion.li>
              ))}
            </ul>
          </>
        )}
      </div>
      <Tooltip id={tooltipId} />
    </>
  );
};

export default memo(ButtonWithMenu);
